import { useMemo, useState } from 'react';

import { Button } from '#/components/Button/Button';
import { H4 } from '#/components/Text/H4';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { getMainChartActiveLayer } from '#/modules/salary/helpers/getMainChartActiveLayer';
import { getMainChartDetails } from '#/modules/salary/helpers/getMainChartDetails';
import { TitleDescriptionModal } from '#/modules/salary/modals/TitleDescription/TitleDescriptionModal';
import type { MainChartData } from '#/modules/salary/types/MainChartData';
import type { Color } from '#/theme/theme';

import { ChartDetail } from './ChartDetail';
import {
  ChartDetailsContainer,
  LeftSection,
  RightSection,
  SplitSection,
  StyledTooltip,
} from './MainChartDetails.styled';

function formatSalary(salary: number, locale: I18nLocale): string {
  return salary.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

type Props = {
  data: MainChartData;
};

export function MainChartDetails({ data }: Props) {
  const { i18n, locale } = useI18n();

  const [showModal, setShowModal] = useState(false);

  const activeChart = getMainChartActiveLayer(data);

  const iconColor: Color =
    activeChart === 'estimatedRange' ? 'brandBlue' : 'brandGreen';

  const chartDetails = useMemo(
    () => getMainChartDetails(i18n, data, activeChart),
    [i18n, data, activeChart],
  );

  const { filtersUsed, submittedData } = data;
  const { cityBucket, yearsOfExperience } = filtersUsed ?? {};
  const locationNotApplied =
    activeChart === 'submissions' &&
    cityBucket !== 'CITY' &&
    submittedData?.city;
  const yearsOfExperienceNotApplied =
    activeChart === 'submissions' &&
    submittedData &&
    yearsOfExperience &&
    submittedData.yearsOfExperience &&
    !yearsOfExperience.includes(submittedData.yearsOfExperience);

  const locationTooltip = locationNotApplied ? (
    <StyledTooltip
      green={activeChart === 'submissions'}
      greyColor
      qaId="location-field-tooltip"
    >
      {cityBucket === 'NATIONAL'
        ? i18n(
            "We don't have enough data yet for your location, so we expanded the search to display national data for the United States.",
          )
        : i18n(
            "We don't currently have enough data yet for your location, so we expanded the search to include other cities with a similar average annual wage.",
          )}
    </StyledTooltip>
  ) : null;

  const yearsOfExperienceTooltip = yearsOfExperienceNotApplied ? (
    <StyledTooltip
      green={activeChart === 'submissions'}
      greyColor
      qaId="years-of-experience-field-tooltip"
    >
      {yearsOfExperience
        ? i18n(
            "We don't have enough data yet within that range for Years of Experience, so we have expanded it to include salary submissions from people with %(yearsOfExperienceUsedMin)s-%(yearsOfExperienceUsedMax)s years of experience.",
            {
              yearsOfExperienceUsedMin: yearsOfExperience[0],
              yearsOfExperienceUsedMax: yearsOfExperience[1],
            },
          )
        : i18n(
            "We don't have enough data yet within that range for Years of Experience, so we have expanded it to include salary submissions from people with any number of years of experience.",
          )}
    </StyledTooltip>
  ) : null;

  return (
    <SplitSection data-qa-id="main-chart-details">
      <LeftSection>
        <H4>{i18n('Your Key Inputs')}</H4>
        <ChartDetailsContainer>
          <ChartDetail iconColor={iconColor}>
            <Button
              type="button"
              qaId="job-title-button"
              noPadding
              onClick={() => setShowModal(true)}
              variant="tertiary"
              size="large"
            >
              <Text style={{ textAlign: 'left' }}>{chartDetails.jobTitle}</Text>
            </Button>
            {showModal ? (
              <TitleDescriptionModal
                onClose={() => setShowModal(false)}
                jobDescription={submittedData?.jobDescription ?? ''}
                jobTitle={submittedData?.jobTitleSelected ?? ''}
              />
            ) : null}
          </ChartDetail>
          <ChartDetail
            iconColor={locationNotApplied ? 'brandYellow' : iconColor}
            iconName={locationNotApplied ? 'double-arrow-small' : undefined}
            tooltip={locationTooltip}
          >
            {chartDetails.location}
          </ChartDetail>
          <ChartDetail
            iconColor={yearsOfExperienceNotApplied ? 'brandYellow' : iconColor}
            iconName={
              yearsOfExperienceNotApplied ? 'double-arrow-small' : undefined
            }
            tooltip={yearsOfExperienceTooltip}
          >
            {chartDetails.yearsOfExperience}
          </ChartDetail>
        </ChartDetailsContainer>
      </LeftSection>
      <RightSection>
        <H4>Understanding Your Results</H4>
        <ChartDetailsContainer>
          <ChartDetail iconColor={iconColor} iconName="us-money-filled">
            {i18n(
              'Most Common Salary Range: {{strong}}%(mostCommonSalaryMin)s to %(mostCommonSalaryMax)s{{/strong}}',
              {
                mostCommonSalaryMin: formatSalary(
                  chartDetails.mostCommonSalaryMin,
                  locale,
                ),
                mostCommonSalaryMax: formatSalary(
                  chartDetails.mostCommonSalaryMax,
                  locale,
                ),
                strong: <strong />,
              },
            )}
          </ChartDetail>
          <ChartDetail iconColor={iconColor} iconName={chartDetails.chartIcon}>
            {chartDetails.description}
          </ChartDetail>
        </ChartDetailsContainer>
      </RightSection>
    </SplitSection>
  );
}
